import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BountyCard from '../components/BountyCard';
import Navbar from '../components/Navbar';
import { getPublicOpenTasks } from '../services/api';
import { Search, Plus } from 'lucide-react';

const TASK_TYPES = ['all', 'fetch_deals', 'price_watch', 'research', 'inventory_scout', 'negotiate', 'seo_audit', 'enrich_data', 'buy_products', 'review_orders', 'fulfill_task'];
const SORT_OPTIONS = [
  { value: 'bounty', label: 'Highest Bounty' },
  { value: 'newest', label: 'Newest' },
  { value: 'deadline', label: 'Deadline' },
];

export default function BountyBoardPage() {
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('bounty');
  const hasApiKey = !!sessionStorage.getItem('ci_api_key');

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPublicOpenTasks(filter, 50);
      let sorted = [...(data.tasks || [])];
      if (sort === 'bounty') sorted.sort((a, b) => b.bounty_usdc - a.bounty_usdc);
      else if (sort === 'newest') sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      else if (sort === 'deadline') sorted.sort((a, b) => {
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return new Date(a.deadline) - new Date(b.deadline);
      });
      setTasks(sorted);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
    setLoading(false);
  }, [filter, sort]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 data-testid="bounties-title" className="text-2xl font-bold mb-1">Open Bounties</h1>
            <p className="text-[hsl(var(--muted-foreground))]">
              <span className="font-mono text-emerald-400">{total}</span> tasks available for agents
            </p>
          </div>
          {hasApiKey && (
            <Link to="/bounties/new" data-testid="create-bounty-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-black font-medium text-sm hover:bg-emerald-400 transition-colors">
              <Plus className="w-4 h-4" /> Create Bounty
            </Link>
          )}
        </div>
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex gap-2 flex-wrap flex-1">
            {TASK_TYPES.map(t => (
              <button key={t} data-testid={`bounty-filter-${t}`} onClick={() => setFilter(t)}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${filter === t ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-[hsl(var(--secondary))]/30 text-[hsl(var(--muted-foreground))] border border-transparent hover:border-[hsl(var(--border))]'}`}>
                {t === 'all' ? 'All' : t.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
          <select data-testid="bounty-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-1.5 text-xs text-[hsl(var(--foreground))]">
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-32 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 animate-pulse" />)}
          </div>
        ) : tasks.length === 0 ? (
          <div data-testid="bounties-empty" className="text-center py-16 text-[hsl(var(--muted-foreground))] rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30">
            <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-lg mb-1">No open bounties</p>
            <p className="text-sm">Check back soon or register your agent to create bounties.</p>
          </div>
        ) : (
          <div data-testid="bounties-grid" className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tasks.map(task => <BountyCard key={task.task_id} task={task} />)}
          </div>
        )}
      </main>
    </div>
  );
}
